import React from 'react';
import { SizingResult } from '../engine/hydraulics';
import { FlowUnit } from '../engine/unitConversions';

// --- Data Contracts for All 5 Calculators ---

export interface WaterPipeReportData {
  inputs: {
    rawFlow: string;
    flowUnit: FlowUnit;
    rawVelocity: string;
    materialId: string;
    materialName: string;
    roughnessMm: number;
    rawLength: string;
    rawTemp: string;
    elbow90: number;
    tee: number;
    valve: number;
    fittingKTotal: number;
  };
  unitSystem: 'metric' | 'imperial';
  result: SizingResult;
}

export interface CopperPipeReportData {
  service: 'domestic_cold' | 'domestic_hot' | 'heating_primary' | 'chilled_water';
  serviceLabel: string;
  rawFlow: string;
  rawLength: string;
  unitSystem: 'metric' | 'imperial';
  maxVelocity: number;
  rows: Array<{
    nominalSize: string;
    innerDiameterMm: number;
    velocityMs: number;
    dpKPaPer10m: number;
    status: string;
  }>;
}

export interface FlowRateReportData {
  calcMode: 'from_velocity' | 'from_dp';
  rawId: string;
  rawVelocity: string;
  rawDp: string;
  rawLength: string;
  results: {
    m3s: number;
    lps: number;
    m3h: number;
    gpmUs: number;
    gpmUk: number;
  };
}

export interface PressureDropReportData {
  materialId: string;
  materialName: string;
  roughnessMm: number;
  rawDiameter: string;
  rawLength: string;
  rawFlow: string;
  elbowCount: number;
  results: {
    velocityMs: number;
    reynoldsNumber: number;
    frictionFactor: number;
    frictionHeadM: number;
    minorHeadM: number;
    totalHeadM: number;
    dpKPa: number;
    dpBar: number;
    dpPsi: number;
    gradientKPaPer100m: number;
  };
}

export interface VelocityReportData {
  rawFlow: string;
  rawId: string;
  results: {
    velocityMs: number;
    velocityFps: number;
    reynoldsNumber: number;
    velocityHeadM: number;
    dynamicPressureKPa: number;
    verdict: string;
  };
}

export type ActiveCalculatorReport =
  | { type: 'water'; data: WaterPipeReportData }
  | { type: 'copper'; data: CopperPipeReportData }
  | { type: 'flow'; data: FlowRateReportData }
  | { type: 'pressure'; data: PressureDropReportData }
  | { type: 'velocity'; data: VelocityReportData }
  | null;

export interface PrintableReportProps {
  reportData: ActiveCalculatorReport | WaterPipeReportData | null;
}

export const PrintableReport: React.FC<PrintableReportProps> = ({ reportData }) => {
  const currentDate = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  // Normalize reportData to discriminated union or null
  const normalizedReport: ActiveCalculatorReport = (() => {
    if (!reportData) return null;
    if ('type' in reportData) return reportData;
    if ('inputs' in reportData && 'result' in reportData) {
      return { type: 'water', data: reportData as WaterPipeReportData };
    }
    return null;
  })();

  if (!normalizedReport) {
    return (
      <div
        id="printable-report"
        className="hidden bg-white text-slate-900 p-8 max-w-4xl mx-auto font-sans"
      >
        <div className="border-b-2 border-slate-900 pb-4 mb-6 flex justify-between items-start">
          <div>
            <h1 className="font-bold text-2xl tracking-tight text-[#10283D]">
              PIPESIZINGCALCULATOR.COM
            </h1>
            <p className="text-xs text-slate-500 font-mono">
              HYDRAULIC DESIGN &amp; PIPE SIZING VERIFICATION SHEET
            </p>
          </div>
          <div className="text-right text-xs">
            <p className="font-mono">{currentDate}</p>
            <p className="text-slate-500">Ref: ENG-PSC-VERIFY</p>
          </div>
        </div>

        <div className="p-6 bg-amber-50 rounded-lg border border-amber-300 text-amber-900 text-sm">
          <p className="font-bold mb-1">NO VALID CALCULATION AVAILABLE</p>
          <p className="text-xs leading-relaxed text-amber-800">
            A verified calculation report cannot be printed because the current calculator inputs
            are invalid, incomplete, or pending calculation. Please return to the calculator, enter
            valid design parameters, and retry.
          </p>
        </div>
      </div>
    );
  }

  // Render based on active calculator type
  if (normalizedReport.type === 'water') {
    const { inputs, result, unitSystem } = normalizedReport.data;
    const isMetric = unitSystem === 'metric';

    const actualVelFormatted = isMetric
      ? `${result.actualVelocityMs.toFixed(2)} m/s`
      : `${(result.actualVelocityMs * 3.28084).toFixed(2)} ft/s`;

    const targetVelFormatted = isMetric
      ? `${inputs.rawVelocity} m/s`
      : `${inputs.rawVelocity} ft/s`;

    const lengthFormatted = isMetric
      ? `${inputs.rawLength} m`
      : `${inputs.rawLength} ft`;

    const tempFormatted = isMetric
      ? `${inputs.rawTemp} °C`
      : `${inputs.rawTemp} °F`;

    const innerDiaFormatted = isMetric
      ? `${result.recommendedPipe.innerDiameterMm.toFixed(2)} mm`
      : `${(result.recommendedPipe.innerDiameterMm / 25.4).toFixed(3)} in`;

    const outerDiaFormatted = isMetric
      ? `${result.recommendedPipe.outerDiameterMm.toFixed(2)} mm`
      : `${(result.recommendedPipe.outerDiameterMm / 25.4).toFixed(3)} in`;

    const wallThicknessFormatted = isMetric
      ? `${result.recommendedPipe.wallThicknessMm.toFixed(2)} mm`
      : `${(result.recommendedPipe.wallThicknessMm / 25.4).toFixed(3)} in`;

    const reqBoreFormatted = isMetric
      ? `${result.requiredDiameterMm.toFixed(2)} mm`
      : `${(result.requiredDiameterMm / 25.4).toFixed(3)} in`;

    const frictionHeadFormatted = isMetric
      ? `${result.frictionHeadLossM.toFixed(2)} m`
      : `${(result.frictionHeadLossM * 3.28084).toFixed(2)} ft`;

    const minorHeadFormatted = isMetric
      ? `${result.minorHeadLossM.toFixed(2)} m`
      : `${(result.minorHeadLossM * 3.28084).toFixed(2)} ft`;

    const totalHeadFormatted = isMetric
      ? `${result.totalHeadLossM.toFixed(2)} m`
      : `${(result.totalHeadLossM * 3.28084).toFixed(2)} ft`;

    const velocityStatus =
      result.actualVelocityMs > 2.4
        ? 'High Velocity (> 2.4 m/s)'
        : result.actualVelocityMs < 0.5
        ? 'Low Velocity (< 0.5 m/s)'
        : 'Optimal Velocity (0.5 – 2.4 m/s)';

    return (
      <div
        id="printable-report"
        className="hidden bg-white text-slate-900 p-8 max-w-4xl mx-auto font-sans text-xs leading-normal"
      >
        {/* Header */}
        <div className="print-avoid-break border-b-2 border-slate-900 pb-4 mb-5 flex justify-between items-start">
          <div>
            <h1 className="font-extrabold text-2xl tracking-tight text-[#10283D]">
              PIPESIZINGCALCULATOR.COM
            </h1>
            <p className="text-[11px] font-semibold text-slate-600 tracking-wide uppercase font-mono mt-0.5">
              Hydraulic Design &amp; Pipe Sizing Verification Sheet
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Calculation Engine: BS EN 806 • Swamee-Jain Darcy-Weisbach • Vogel Viscosity
            </p>
          </div>
          <div className="text-right text-[11px]">
            <p className="font-mono font-bold text-slate-800">{currentDate}</p>
            <p className="text-slate-500 font-mono">Ref: ENG-PSC-VERIFY</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-300">
              {isMetric ? 'Metric Units (SI)' : 'Imperial Units'}
            </span>
          </div>
        </div>

        {/* Verification Statement */}
        <div className="print-avoid-break p-3.5 bg-slate-50 rounded-lg border border-slate-200 mb-5">
          <p className="font-bold text-slate-800 text-[11px] uppercase tracking-wide mb-1">
            Engineering Calculation Methodology
          </p>
          <p className="text-[11px] text-slate-600 leading-relaxed">
            Calculations are deterministically performed by the client-side calculation engine.
            Required diameter is computed via BS EN 806 continuity sizing criteria. Water density and dynamic
            viscosity are computed from operating temperature using Vogel formulation. Pipe friction head loss
            is solved explicitly via the Swamee-Jain equation for the Darcy-Weisbach friction factor, combined
            with 2-K equivalent minor fitting losses.
          </p>
        </div>

        {/* Selected Pipe Recommendation Banner */}
        <div className="print-avoid-break p-4 rounded-xl border-2 border-[#10283D] bg-slate-50 mb-5">
          <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-200">
            <div>
              <span className="text-[10px] font-bold tracking-widest uppercase text-[#087F9C]">
                RECOMMENDED COMMERCIAL PIPE SELECTION
              </span>
              <div className="font-extrabold text-3xl text-[#10283D] leading-tight">
                {result.recommendedPipe.nominalSize}
              </div>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-bold uppercase text-slate-500 block">Specification Standard</span>
              <span className="font-bold text-xs text-slate-800">
                {result.recommendedPipe.standard}
              </span>
              <p className="text-[10px] text-slate-500">{inputs.materialName}</p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3 text-center pt-1">
            <div className="p-2 bg-white rounded border border-slate-200">
              <span className="block text-[10px] text-slate-500 font-semibold uppercase">Internal Bore (ID)</span>
              <span className="font-bold text-sm text-slate-900">{innerDiaFormatted}</span>
            </div>
            <div className="p-2 bg-white rounded border border-slate-200">
              <span className="block text-[10px] text-slate-500 font-semibold uppercase">Outer Diameter (OD)</span>
              <span className="font-bold text-sm text-slate-900">{outerDiaFormatted}</span>
            </div>
            <div className="p-2 bg-white rounded border border-slate-200">
              <span className="block text-[10px] text-slate-500 font-semibold uppercase">Wall Thickness</span>
              <span className="font-bold text-sm text-slate-900">{wallThicknessFormatted}</span>
            </div>
            <div className="p-2 bg-white rounded border border-slate-200">
              <span className="block text-[10px] text-slate-500 font-semibold uppercase">Theoretical Min Bore</span>
              <span className="font-bold text-sm text-[#087F9C]">{reqBoreFormatted}</span>
            </div>
          </div>
        </div>

        {/* Section 1 & 2: Design Inputs & Hydraulic Performance */}
        <div className="print-avoid-break grid grid-cols-2 gap-4 mb-5">
          {/* Design Inputs Table */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-[#10283D] text-white px-3 py-1.5 font-bold text-[11px] uppercase tracking-wide">
              1. Design Input Parameters
            </div>
            <table className="w-full text-[11px]">
              <tbody>
                <tr className="border-b border-slate-100 bg-white">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Design Flow Rate</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">
                    {inputs.rawFlow} {inputs.flowUnit}
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Target Design Velocity</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">{targetVelFormatted}</td>
                </tr>
                <tr className="border-b border-slate-100 bg-white">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Pipe Run Length</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">{lengthFormatted}</td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Water Temperature</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">{tempFormatted}</td>
                </tr>
                <tr className="border-b border-slate-100 bg-white">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Material Roughness (ε)</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right font-mono">
                    {inputs.roughnessMm} mm
                  </td>
                </tr>
                <tr className="bg-slate-50/50">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Minor Loss Fittings</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">
                    {inputs.elbow90} elbows, {inputs.tee} tees, {inputs.valve} valves (ΣK = {inputs.fittingKTotal.toFixed(2)})
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Hydraulic Performance Table */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-[#10283D] text-white px-3 py-1.5 font-bold text-[11px] uppercase tracking-wide">
              2. Hydraulic Performance Results
            </div>
            <table className="w-full text-[11px]">
              <tbody>
                <tr className="border-b border-slate-100 bg-white">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Actual Flow Velocity</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">
                    {actualVelFormatted}
                    <span className="block text-[9px] font-normal text-slate-500">{velocityStatus}</span>
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Friction Head Loss (hf)</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">{frictionHeadFormatted}</td>
                </tr>
                <tr className="border-b border-slate-100 bg-white">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Fittings Head Loss (hm)</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">{minorHeadFormatted}</td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Total Head Loss</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">{totalHeadFormatted}</td>
                </tr>
                <tr className="border-b border-slate-100 bg-white">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Total Pressure Drop (ΔP)</td>
                  <td className="py-1.5 px-3 font-bold text-slate-900 text-right">
                    {result.pressureDropKPa.toFixed(1)} kPa
                    <span className="block text-[9px] font-normal text-slate-500 font-mono">
                      {result.pressureDropBar.toFixed(3)} bar / {result.pressureDropPsi.toFixed(2)} psi
                    </span>
                  </td>
                </tr>
                <tr className="bg-slate-50/50">
                  <td className="py-1.5 px-3 font-medium text-slate-600">Hydraulic Gradient</td>
                  <td className="py-1.5 px-3 font-bold text-[#087F9C] text-right font-mono">
                    {result.hydraulicGradientKPaPer100m.toFixed(1)} kPa/100m
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 3: Fluid Mechanics & Verification Parameters */}
        <div className="print-avoid-break border border-slate-200 rounded-lg overflow-hidden mb-5">
          <div className="bg-[#10283D] text-white px-3 py-1.5 font-bold text-[11px] uppercase tracking-wide">
            3. Fluid Dynamics &amp; Mechanics Verification
          </div>
          <div className="grid grid-cols-4 gap-0 divide-x divide-slate-200 text-center bg-white text-[11px]">
            <div className="p-2.5">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Reynolds Number</span>
              <span className="font-mono font-bold text-sm text-slate-800">
                {result.reynoldsNumber.toLocaleString('en-US')}
              </span>
              <span className="block text-[10px] font-semibold text-emerald-700 mt-0.5">
                {result.flowRegime} Flow
              </span>
            </div>
            <div className="p-2.5">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Friction Factor (f)</span>
              <span className="font-mono font-bold text-sm text-slate-800">
                {result.frictionFactor.toFixed(4)}
              </span>
              <span className="block text-[10px] text-slate-500 mt-0.5">Swamee-Jain</span>
            </div>
            <div className="p-2.5">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Fluid Density (ρ)</span>
              <span className="font-mono font-bold text-sm text-slate-800">
                {result.fluidProperties.density.toFixed(1)} kg/m³
              </span>
              <span className="block text-[10px] text-slate-500 mt-0.5">
                @ {result.fluidProperties.temperatureC}°C
              </span>
            </div>
            <div className="p-2.5">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Kinematic Viscosity (ν)</span>
              <span className="font-mono font-bold text-sm text-slate-800">
                {result.fluidProperties.kinematicViscosity.toExponential(3)}
              </span>
              <span className="block text-[10px] text-slate-500 mt-0.5">m²/s</span>
            </div>
          </div>
        </div>

        {/* Section 4: Engineering Warnings */}
        {result.warnings.length > 0 && (
          <div className="print-avoid-break mb-5 p-3.5 bg-amber-50 rounded-lg border border-amber-300">
            <p className="font-bold text-amber-900 text-[11px] uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
              <span>⚠️</span>
              <span>Engineering Design Advisories ({result.warnings.length})</span>
            </p>
            <ul className="space-y-1 text-[11px] text-amber-800 pl-4 list-disc">
              {result.warnings.map((w, idx) => (
                <li key={idx} className="leading-tight">
                  {w}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Section 5: Mathematical Proof & Working Formulas */}
        <div className="print-avoid-break border border-slate-200 rounded-lg overflow-hidden mb-5">
          <div className="bg-[#10283D] text-white px-3 py-1.5 font-bold text-[11px] uppercase tracking-wide">
            4. Mathematical Working &amp; Governing Equations
          </div>
          <div className="p-3 bg-slate-50/60 space-y-2.5 font-mono text-[10px] text-slate-700">
            <div>
              <span className="font-bold text-slate-900 block font-sans text-[11px]">
                1. Continuity Theoretical Bore Diameter (D_req):
              </span>
              <div className="p-1.5 bg-white rounded border border-slate-200 whitespace-pre-wrap">
                {result.calculationsExplanation.continuityFormula.trim()}
              </div>
            </div>
            <div>
              <span className="font-bold text-slate-900 block font-sans text-[11px]">
                2. Actual Pipe Velocity Check (v):
              </span>
              <div className="p-1.5 bg-white rounded border border-slate-200 whitespace-pre-wrap">
                {result.calculationsExplanation.velocityCheck.trim()}
              </div>
            </div>
            <div>
              <span className="font-bold text-slate-900 block font-sans text-[11px]">
                3. Reynolds Number Check (Re):
              </span>
              <div className="p-1.5 bg-white rounded border border-slate-200 whitespace-pre-wrap">
                {result.calculationsExplanation.reynoldsFormula.trim()}
              </div>
            </div>
            <div>
              <span className="font-bold text-slate-900 block font-sans text-[11px]">
                4. Darcy-Weisbach Friction Factor (Swamee-Jain f):
              </span>
              <div className="p-1.5 bg-white rounded border border-slate-200 whitespace-pre-wrap">
                {result.calculationsExplanation.frictionFormula.trim()}
              </div>
            </div>
            <div>
              <span className="font-bold text-slate-900 block font-sans text-[11px]">
                5. Darcy-Weisbach Total Head Loss (hf + hm):
              </span>
              <div className="p-1.5 bg-white rounded border border-slate-200 whitespace-pre-wrap">
                {result.calculationsExplanation.darcyFormula.trim()}
              </div>
            </div>
          </div>
        </div>

        {/* Document Footer */}
        <div className="print-avoid-break border-t border-slate-300 pt-3 text-[10px] text-slate-500 flex justify-between items-center">
          <span>Generated via PipeSizingCalculator Engineering Calculation Engine v2.4</span>
          <span>Deterministic Client-Side Verification • Verified with BS EN 806 &amp; CIBSE Guide G</span>
        </div>
      </div>
    );
  }

  // Copper Pipe Sizing Report
  if (normalizedReport.type === 'copper') {
    const { serviceLabel, rawFlow, rawLength, unitSystem, maxVelocity, rows } = normalizedReport.data;
    const isMetric = unitSystem === 'metric';

    return (
      <div
        id="printable-report"
        className="hidden bg-white text-slate-900 p-8 max-w-4xl mx-auto font-sans text-xs leading-normal"
      >
        <div className="print-avoid-break border-b-2 border-slate-900 pb-4 mb-5 flex justify-between items-start">
          <div>
            <h1 className="font-extrabold text-2xl tracking-tight text-[#10283D]">
              PIPESIZINGCALCULATOR.COM
            </h1>
            <p className="text-[11px] font-semibold text-amber-700 tracking-wide uppercase font-mono mt-0.5">
              Copper Pipe Sizing Specification Sheet • BS EN 1057 &amp; ASTM B88
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              CIBSE Guide G Velocity &amp; Acoustic Erosion Criteria
            </p>
          </div>
          <div className="text-right text-[11px]">
            <p className="font-mono font-bold text-slate-800">{currentDate}</p>
            <p className="text-slate-500 font-mono">Ref: ENG-PSC-COPPER</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-300">
              {isMetric ? 'Metric (SI)' : 'Imperial'}
            </span>
          </div>
        </div>

        <div className="print-avoid-break grid grid-cols-3 gap-3 mb-5">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Service Application</span>
            <span className="font-bold text-sm text-[#10283D]">{serviceLabel}</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Design Flow Rate</span>
            <span className="font-bold text-sm text-slate-900">{rawFlow} {isMetric ? 'L/s' : 'GPM'}</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Run Length</span>
            <span className="font-bold text-sm text-slate-900">{rawLength} {isMetric ? 'm' : 'ft'} (Max v: {maxVelocity} m/s)</span>
          </div>
        </div>

        <div className="print-avoid-break border border-slate-200 rounded-lg overflow-hidden mb-5">
          <div className="bg-[#10283D] text-white px-3 py-1.5 font-bold text-[11px] uppercase tracking-wide">
            Commercial Copper Tube Evaluation Table (BS EN 1057 Table X)
          </div>
          <table className="w-full text-[11px] text-left">
            <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-bold uppercase">
              <tr>
                <th className="py-2 px-3">Nominal Size</th>
                <th className="py-2 px-3">Internal Bore (ID)</th>
                <th className="py-2 px-3">Flow Velocity</th>
                <th className="py-2 px-3">Pressure Loss / 10m</th>
                <th className="py-2 px-3">CIBSE Compliance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.nominalSize}>
                  <td className="py-2 px-3 font-bold text-slate-900">{row.nominalSize}</td>
                  <td className="py-2 px-3 font-mono">{row.innerDiameterMm.toFixed(1)} mm</td>
                  <td className="py-2 px-3 font-mono font-bold">{row.velocityMs.toFixed(2)} m/s</td>
                  <td className="py-2 px-3 font-mono">{row.dpKPaPer10m.toFixed(1)} kPa</td>
                  <td className="py-2 px-3">
                    <span className="font-semibold">{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="print-avoid-break p-3.5 bg-slate-50 rounded-lg border border-slate-200 mb-5">
          <p className="font-bold text-slate-800 text-[11px] uppercase tracking-wide mb-1">
            CIBSE Guide G &amp; BS EN 1057 Engineering Standards
          </p>
          <p className="text-[11px] text-slate-600 leading-relaxed">
            Copper pipe sizing requires careful velocity control to avoid premature tube thinning and impingement erosion,
            especially at elbows and tees. Recommended velocity upper limits: Domestic Cold Water = 2.0 m/s; Domestic Hot Water (60°C) = 1.2 m/s;
            Primary Heating Circuits = 1.0 m/s (acoustic mitigation); Chilled Water Circuits = 1.5 m/s.
          </p>
        </div>

        <div className="print-avoid-break border-t border-slate-300 pt-3 text-[10px] text-slate-500 flex justify-between items-center">
          <span>Generated via PipeSizingCalculator Engineering Hydraulic Suite v2.4</span>
          <span>Deterministic Client-Side Verification • Verified with BS EN 1057 Table X &amp; ASTM B88</span>
        </div>
      </div>
    );
  }

  // Flow Rate Calculator Report
  if (normalizedReport.type === 'flow') {
    const { calcMode, rawId, rawVelocity, rawDp, rawLength, results } = normalizedReport.data;

    return (
      <div
        id="printable-report"
        className="hidden bg-white text-slate-900 p-8 max-w-4xl mx-auto font-sans text-xs leading-normal"
      >
        <div className="print-avoid-break border-b-2 border-slate-900 pb-4 mb-5 flex justify-between items-start">
          <div>
            <h1 className="font-extrabold text-2xl tracking-tight text-[#10283D]">
              PIPESIZINGCALCULATOR.COM
            </h1>
            <p className="text-[11px] font-semibold text-[#087F9C] tracking-wide uppercase font-mono mt-0.5">
              Pipe Flow Rate &amp; Hydraulic Capacity Report
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Continuity Principle • Darcy-Weisbach Head Loss Inversion
            </p>
          </div>
          <div className="text-right text-[11px]">
            <p className="font-mono font-bold text-slate-800">{currentDate}</p>
            <p className="text-slate-500 font-mono">Ref: ENG-PSC-FLOW</p>
          </div>
        </div>

        <div className="print-avoid-break grid grid-cols-3 gap-3 mb-5">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Calculation Mode</span>
            <span className="font-bold text-sm text-[#10283D]">
              {calcMode === 'from_velocity' ? 'From Fluid Velocity' : 'From Allowable Head Loss'}
            </span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Internal Bore (ID)</span>
            <span className="font-bold text-sm text-slate-900">{rawId} mm</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Input Constraint</span>
            <span className="font-bold text-sm text-slate-900">
              {calcMode === 'from_velocity'
                ? `Velocity: ${rawVelocity} m/s`
                : `ΔP: ${rawDp} kPa across ${rawLength} m`}
            </span>
          </div>
        </div>

        <div className="print-avoid-break border border-slate-200 rounded-lg overflow-hidden mb-5">
          <div className="bg-[#10283D] text-white px-3 py-1.5 font-bold text-[11px] uppercase tracking-wide">
            Volumetric Flow Rate &amp; Hydraulic Capacity Results
          </div>
          <div className="grid grid-cols-4 gap-0 divide-x divide-slate-200 text-center bg-white">
            <div className="p-4">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Litres per Second</span>
              <span className="font-extrabold text-xl text-[#10283D] font-mono">{results.lps.toFixed(3)}</span>
              <span className="block text-[10px] text-slate-500 mt-0.5">L/s</span>
            </div>
            <div className="p-4">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Cubic Metres / Hour</span>
              <span className="font-extrabold text-xl text-[#10283D] font-mono">{results.m3h.toFixed(2)}</span>
              <span className="block text-[10px] text-slate-500 mt-0.5">m³/h</span>
            </div>
            <div className="p-4">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">US Gallons / Min</span>
              <span className="font-extrabold text-xl text-[#087F9C] font-mono">{results.gpmUs.toFixed(2)}</span>
              <span className="block text-[10px] text-slate-500 mt-0.5">US GPM</span>
            </div>
            <div className="p-4">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">UK Gallons / Min</span>
              <span className="font-extrabold text-xl text-[#10283D] font-mono">{results.gpmUk.toFixed(2)}</span>
              <span className="block text-[10px] text-slate-500 mt-0.5">UK GPM</span>
            </div>
          </div>
        </div>

        <div className="print-avoid-break p-3.5 bg-slate-50 rounded-lg border border-slate-200 mb-5 font-mono text-[11px]">
          <p className="font-bold text-slate-800 text-[11px] uppercase tracking-wide font-sans mb-1">
            Governing Continuity Formulation
          </p>
          <p className="text-slate-600 leading-relaxed font-sans">
            Volumetric flow rate Q is computed via standard continuity equation:
          </p>
          <div className="mt-2 p-2 bg-white rounded border border-slate-200">
            Q = v · A = v · (π · D² / 4)
            <br />
            Internal Cross-Sectional Area (A) = {((Math.PI * Math.pow(parseFloat(rawId) / 1000, 2)) / 4).toExponential(4)} m²
            <br />
            Mass Flow Rate (Water @ 20°C) ≈ {(results.lps * 0.9982).toFixed(3)} kg/s
          </div>
        </div>

        <div className="print-avoid-break border-t border-slate-300 pt-3 text-[10px] text-slate-500 flex justify-between items-center">
          <span>Generated via PipeSizingCalculator Engineering Calculation Engine v2.4</span>
          <span>Deterministic Client-Side Verification • International Metric and Imperial Standards</span>
        </div>
      </div>
    );
  }

  // Pressure Drop Calculator Report
  if (normalizedReport.type === 'pressure') {
    const { materialName, roughnessMm, rawDiameter, rawLength, rawFlow, elbowCount, results } = normalizedReport.data;

    return (
      <div
        id="printable-report"
        className="hidden bg-white text-slate-900 p-8 max-w-4xl mx-auto font-sans text-xs leading-normal"
      >
        <div className="print-avoid-break border-b-2 border-slate-900 pb-4 mb-5 flex justify-between items-start">
          <div>
            <h1 className="font-extrabold text-2xl tracking-tight text-[#10283D]">
              PIPESIZINGCALCULATOR.COM
            </h1>
            <p className="text-[11px] font-semibold text-blue-700 tracking-wide uppercase font-mono mt-0.5">
              Darcy-Weisbach Pressure Drop &amp; Friction Loss Report
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Swamee-Jain Explicit Friction Factor • Colebrook-White Equivalent
            </p>
          </div>
          <div className="text-right text-[11px]">
            <p className="font-mono font-bold text-slate-800">{currentDate}</p>
            <p className="text-slate-500 font-mono">Ref: ENG-PSC-DARCY</p>
          </div>
        </div>

        <div className="print-avoid-break grid grid-cols-4 gap-3 mb-5">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Pipe Material</span>
            <span className="font-bold text-sm text-[#10283D] truncate block">{materialName}</span>
            <span className="text-[10px] text-slate-400 font-mono">ε = {roughnessMm} mm</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Internal Diameter</span>
            <span className="font-bold text-sm text-slate-900">{rawDiameter} mm</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Run Length</span>
            <span className="font-bold text-sm text-slate-900">{rawLength} m</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Flow Rate</span>
            <span className="font-bold text-sm text-slate-900">{rawFlow} L/s</span>
          </div>
        </div>

        <div className="print-avoid-break grid grid-cols-3 gap-3 mb-5">
          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 text-center">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Total Pressure Drop</span>
            <span className="font-extrabold text-2xl text-[#10283D] font-mono">{results.dpKPa.toFixed(1)} kPa</span>
            <span className="block text-[10px] text-slate-500 font-mono mt-0.5">
              {results.dpBar.toFixed(3)} bar / {results.dpPsi.toFixed(2)} psi
            </span>
          </div>
          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 text-center">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Total Head Loss</span>
            <span className="font-extrabold text-2xl text-[#087F9C] font-mono">{results.totalHeadM.toFixed(2)} m</span>
            <span className="block text-[10px] text-slate-500 font-mono mt-0.5">
              {(results.totalHeadM * 3.28084).toFixed(2)} ft of water
            </span>
          </div>
          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 text-center">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Hydraulic Gradient</span>
            <span className="font-extrabold text-2xl text-slate-800 font-mono">{results.gradientKPaPer100m.toFixed(1)}</span>
            <span className="block text-[10px] text-slate-500 font-mono mt-0.5">kPa / 100m</span>
          </div>
        </div>

        <div className="print-avoid-break border border-slate-200 rounded-lg overflow-hidden mb-5">
          <div className="bg-[#10283D] text-white px-3 py-1.5 font-bold text-[11px] uppercase tracking-wide">
            Fluid Dynamics &amp; Friction Factor Verification
          </div>
          <div className="grid grid-cols-4 gap-0 divide-x divide-slate-200 text-center bg-white text-[11px]">
            <div className="p-2.5">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Flow Velocity</span>
              <span className="font-mono font-bold text-sm text-slate-800">{results.velocityMs.toFixed(2)} m/s</span>
            </div>
            <div className="p-2.5">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Reynolds Number</span>
              <span className="font-mono font-bold text-sm text-slate-800">{results.reynoldsNumber.toLocaleString()}</span>
            </div>
            <div className="p-2.5">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Friction Factor (f)</span>
              <span className="font-mono font-bold text-sm text-slate-800">{results.frictionFactor.toFixed(4)}</span>
            </div>
            <div className="p-2.5">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Minor Fittings</span>
              <span className="font-mono font-bold text-sm text-slate-800">{elbowCount} 90° elbows</span>
            </div>
          </div>
        </div>

        <div className="print-avoid-break p-3.5 bg-slate-50 rounded-lg border border-slate-200 mb-5 font-mono text-[10px]">
          <p className="font-bold text-slate-800 text-[11px] uppercase tracking-wide font-sans mb-1">
            Governing Darcy-Weisbach Equation
          </p>
          <div className="p-2 bg-white rounded border border-slate-200">
            hf = f · (L / D) · (v² / 2g) = {results.frictionHeadM.toFixed(3)} m head
            <br />
            hm = ΣK · (v² / 2g) = {results.minorHeadM.toFixed(3)} m head
            <br />
            ΔP = ρ · g · (hf + hm) = {(results.dpKPa * 1000).toFixed(0)} Pa = {results.dpKPa.toFixed(2)} kPa
          </div>
        </div>

        <div className="print-avoid-break border-t border-slate-300 pt-3 text-[10px] text-slate-500 flex justify-between items-center">
          <span>Generated via PipeSizingCalculator Engineering Calculation Engine v2.4</span>
          <span>Deterministic Client-Side Verification • Standard Water Properties @ 20°C</span>
        </div>
      </div>
    );
  }

  // Velocity Calculator Report
  if (normalizedReport.type === 'velocity') {
    const { rawFlow, rawId, results } = normalizedReport.data;

    return (
      <div
        id="printable-report"
        className="hidden bg-white text-slate-900 p-8 max-w-4xl mx-auto font-sans text-xs leading-normal"
      >
        <div className="print-avoid-break border-b-2 border-slate-900 pb-4 mb-5 flex justify-between items-start">
          <div>
            <h1 className="font-extrabold text-2xl tracking-tight text-[#10283D]">
              PIPESIZINGCALCULATOR.COM
            </h1>
            <p className="text-[11px] font-semibold text-emerald-700 tracking-wide uppercase font-mono mt-0.5">
              Pipe Flow Velocity &amp; CIBSE Standards Report
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              CIBSE Guide G • BS EN 806 Acoustic &amp; Erosion Standards
            </p>
          </div>
          <div className="text-right text-[11px]">
            <p className="font-mono font-bold text-slate-800">{currentDate}</p>
            <p className="text-slate-500 font-mono">Ref: ENG-PSC-VELOCITY</p>
          </div>
        </div>

        <div className="print-avoid-break grid grid-cols-2 gap-4 mb-5">
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Design Flow Rate</span>
            <span className="font-bold text-sm text-[#10283D]">{rawFlow} L/s</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <span className="block text-[10px] uppercase font-bold text-slate-400">Internal Diameter (Bore)</span>
            <span className="font-bold text-sm text-[#10283D]">{rawId} mm</span>
          </div>
        </div>

        <div className="print-avoid-break p-4 rounded-xl border-2 border-[#10283D] bg-slate-50 mb-5 flex justify-between items-center">
          <div>
            <span className="text-[10px] font-bold tracking-widest uppercase text-[#087F9C]">
              Calculated Flow Velocity
            </span>
            <div className="font-extrabold text-3xl text-[#10283D] font-mono leading-tight">
              {results.velocityMs.toFixed(2)} m/s
              <span className="text-xs text-slate-500 font-sans ml-3 font-normal">
                ({results.velocityFps.toFixed(2)} ft/s)
              </span>
            </div>
          </div>
          <div className="text-right">
            <span className="text-[10px] font-bold uppercase text-slate-500 block">CIBSE / BS EN 806 Verdict</span>
            <span className="font-extrabold text-sm px-3 py-1 rounded-lg bg-white border border-slate-300 text-slate-900 inline-block mt-1">
              {results.verdict}
            </span>
          </div>
        </div>

        <div className="print-avoid-break border border-slate-200 rounded-lg overflow-hidden mb-5">
          <div className="bg-[#10283D] text-white px-3 py-1.5 font-bold text-[11px] uppercase tracking-wide">
            Hydraulic Dynamics &amp; Pressure Characteristics
          </div>
          <div className="grid grid-cols-3 gap-0 divide-x divide-slate-200 text-center bg-white">
            <div className="p-3">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Velocity Head (v² / 2g)</span>
              <span className="font-mono font-bold text-sm text-slate-800">{results.velocityHeadM.toFixed(3)} m</span>
              <span className="block text-[10px] text-slate-400 mt-0.5">m of water column</span>
            </div>
            <div className="p-3">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Dynamic Pressure (½ρv²)</span>
              <span className="font-mono font-bold text-sm text-slate-800">{results.dynamicPressureKPa.toFixed(2)} kPa</span>
              <span className="block text-[10px] text-slate-400 mt-0.5">fluid kinetic energy</span>
            </div>
            <div className="p-3">
              <span className="block text-[10px] text-slate-400 font-bold uppercase">Reynolds Number</span>
              <span className="font-mono font-bold text-sm text-slate-800">{results.reynoldsNumber.toLocaleString()}</span>
              <span className="block text-[10px] text-slate-400 mt-0.5">turbulent regime</span>
            </div>
          </div>
        </div>

        <div className="print-avoid-break p-3.5 bg-slate-50 rounded-lg border border-slate-200 mb-5">
          <p className="font-bold text-slate-800 text-[11px] uppercase tracking-wide mb-1">
            CIBSE Guide G &amp; BS EN 806 Velocity Guidelines
          </p>
          <ul className="list-disc pl-5 space-y-1 text-slate-600 text-[11px]">
            <li><strong>v &lt; 0.5 m/s:</strong> Low velocity. Risk of bio-film accumulation, sedimentation, and oversized piping expenditure.</li>
            <li><strong>0.5 – 2.4 m/s:</strong> Optimal engineering range for general water supply runs. Ensures self-cleansing without cavitation noise.</li>
            <li><strong>v &gt; 2.4 m/s:</strong> High velocity. Risk of acoustic nuisance, hydraulic water hammer shock, and accelerated erosion corrosion.</li>
          </ul>
        </div>

        <div className="print-avoid-break border-t border-slate-300 pt-3 text-[10px] text-slate-500 flex justify-between items-center">
          <span>Generated via PipeSizingCalculator Engineering Calculation Engine v2.4</span>
          <span>Deterministic Client-Side Verification • Verified with CIBSE Guide G Standards</span>
        </div>
      </div>
    );
  }

  return null;
};
